import Button from 'devextreme-react/button';
import CheckBox from 'devextreme-react/check-box';
import Popup from 'devextreme-react/popup';
import { useResponsivePopupOptions } from '../shell/useResponsivePopup';
import { PATIENT_STATUS_LABELS, type PatientStatus } from './patientTrackingTypes';
import type { KanbanLayoutConfig } from './patientTrackingKanban';

interface PatientKanbanConfigPopupProps {
  visible: boolean;
  config: KanbanLayoutConfig;
  onClose: () => void;
  onChange: (config: KanbanLayoutConfig) => void;
}

function moveColumn(order: PatientStatus[], id: PatientStatus, direction: -1 | 1): PatientStatus[] {
  const index = order.indexOf(id);
  const nextIndex = index + direction;
  if (index < 0 || nextIndex < 0 || nextIndex >= order.length) return order;
  const next = [...order];
  [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
  return next;
}

export function PatientKanbanConfigPopup({
  visible,
  config,
  onClose,
  onChange
}: PatientKanbanConfigPopupProps) {
  const hidden = new Set(config.hidden);
  const popupOptions = useResponsivePopupOptions({
    width: 380,
    height: 'auto'
  });

  const toggleColumn = (status: PatientStatus) => {
    const nextHidden = new Set(config.hidden);
    if (nextHidden.has(status)) nextHidden.delete(status);
    else nextHidden.add(status);
    onChange({ ...config, hidden: [...nextHidden] });
  };

  const reset = () => {
    onChange({ order: [...config.order], hidden: [] });
  };

  return (
    <Popup
      visible={visible}
      onHiding={onClose}
      title="Kanban columns"
      showCloseButton
      {...popupOptions}
      maxHeight={popupOptions.fullScreen ? undefined : '80vh'}
    >
      <div className="patient-kanban-config">
        <p className="patient-kanban-config__intro">
          Show, hide, and reorder Kanban columns. Resolved and Lost columns only show cards from the
          last week.
        </p>
        <div className="patient-kanban-config__header">
          <strong>Columns</strong>
          <Button text="Reset" stylingMode="text" onClick={reset} />
        </div>
        <div className="patient-kanban-config__list">
          {config.order.map((status, index) => (
            <div key={status} className="patient-kanban-config__row">
              <CheckBox
                value={!hidden.has(status)}
                text={PATIENT_STATUS_LABELS[status]}
                onValueChanged={() => toggleColumn(status)}
              />
              <div>
                <Button
                  icon="chevronup"
                  hint={`Move ${PATIENT_STATUS_LABELS[status]} left`}
                  stylingMode="text"
                  disabled={index === 0}
                  onClick={() => onChange({ ...config, order: moveColumn(config.order, status, -1) })}
                />
                <Button
                  icon="chevrondown"
                  hint={`Move ${PATIENT_STATUS_LABELS[status]} right`}
                  stylingMode="text"
                  disabled={index === config.order.length - 1}
                  onClick={() => onChange({ ...config, order: moveColumn(config.order, status, 1) })}
                />
              </div>
            </div>
          ))}
        </div>
        <div className="patient-kanban-config__footer">
          <Button text="Done" type="default" onClick={onClose} />
        </div>
      </div>
    </Popup>
  );
}
